import { yupResolver } from "@hookform/resolvers/yup";
import { useEffect } from "react";
import { FormProvider, useForm } from "react-hook-form";
import DeleteIcon from "@mui/icons-material/Delete";
import {
  ADDRESS_OPTIONAL,
  INFORMATION,
  OPENING_TIMES,
} from "../../Layout/Tabs/MdmTabPanelConstants";
import PageHeader from "../../Layout/Header/PageHeader";
import PageHeaderButton from "../../Layout/Header/PageHeaderButton";
import { MdmTabs } from "../../Layout/Tabs/MdmTabs";
import Supplier from "../Supplier";
import { supplierDefaultValues } from "./supplierDefaultValues";
import { SupplierForm } from "./SupplierForm/SupplierForm";
import { SupplierOptionalForm } from "./SupplierOptionalForm/SupplierOptionalForm";
import { supplierSchema } from "./supplierSchema";
import PageHeaderIconButton from "../../Layout/Header/PageHeaderIconButton";
import { confirm } from "../../Layout/Dialog/ConfirmDialog";
import { isFormSubmitForbidden } from "../../Forms/isFormSubmitForbidden";
import { resetFormStateAndKeepFormValues } from "../../Forms/resetFormStateAndKeepFormValues";
import { SupplierOpeningTimesParentForm } from "./OpeningTimesForm/SupplierOpeningTimesParentForm";
import { useIsUserInRoles } from "../../Auth/useIsUserInRoles";
import { AuthorizationPolicies } from "../../Auth/AuthorizationPolicy";

type Props = {
  title: string;
  supplier: Supplier;
  onSave: (supplier: Supplier) => void;
  disabledField?: Array<string>;
  onDelete?: (supplierId: number | null | undefined) => void;
};

export const SupplierFormTabs = ({ title, supplier, onSave, disabledField, onDelete }: Props) => {
  const isUserAdminNetworkLaunch = useIsUserInRoles(
    AuthorizationPolicies.RequireAdminNetworkLaunchRoles
  );

  const methods = useForm<Supplier>({
    defaultValues: supplierDefaultValues,
    resolver: yupResolver(supplierSchema),
    mode: "all",
    disabled: !isUserAdminNetworkLaunch,
  });

  useEffect(() => {
    methods.reset(supplier);

    return () => methods.reset(supplierDefaultValues);
  }, [supplier, methods, methods.reset]);

  const onDeleteWithConfirmation = async () => {
    const isConfirmed = await confirm(
      `Do you really want to delete supplier with Id ${supplier?.Id}?`
    );
    if (!isConfirmed || !onDelete) {
      return;
    }
    await onDelete(supplier?.Id);
  };

  return (
    <>
      <PageHeader headerText={title}>
        {onDelete !== undefined && (
          <PageHeaderIconButton
            onClick={() => onDeleteWithConfirmation()}
            color="secondary"
            disabled={!isUserAdminNetworkLaunch}
          >
            <DeleteIcon />
          </PageHeaderIconButton>
        )}
        <PageHeaderButton
          onClick={methods.handleSubmit((values: Supplier) => {
            onSave(values);
            resetFormStateAndKeepFormValues(methods);
          })}
          disabled={isFormSubmitForbidden(methods.formState)}
        >
          Save
        </PageHeaderButton>
      </PageHeader>

      <FormProvider {...methods}>
        <MdmTabs
          tabs={[
            {
              index: 0,
              label: INFORMATION,
              content: <SupplierForm disabledFields={disabledField} />,
            },
            {
              index: 1,
              label: ADDRESS_OPTIONAL,
              content: <SupplierOptionalForm disabledFields={disabledField} />,
              disabled: !supplier,
            },
            {
              index: 2,
              label: OPENING_TIMES,
              content: <SupplierOpeningTimesParentForm parentId={supplier.Id ?? 0} />,
              disabled: !supplier,
            },
          ]}
        />
      </FormProvider>
    </>
  );
};
